import React, { useId } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { RadioGroup } from '@/fluent-adapter';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import { Warning24Regular as AlertTriangle } from '@fluentui/react-icons';
import type { TypeOfRequestForm } from '../../store/types';

interface TypeOfRequestStepProps {
  formData: TypeOfRequestForm;
  onFormChange: (data: Partial<TypeOfRequestForm>) => void;
  errors?: Record<string, string>;
  isUSA?: boolean;
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXXL },
  header: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  title: { fontSize: '20px', fontWeight: 600 },
  muted: { color: tokens.colorNeutralForeground3 },
  errorText: { color: tokens.colorStatusDangerForeground1 },
  sectionTitle: { fontSize: '16px', fontWeight: 600 },
  sectionContent: { ...shorthands.padding('0px') },
  emergencyTitle: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    fontSize: '16px',
    fontWeight: 600,
  },
  emergencyIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '20px',
  },
  emergencyText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
    '& a': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'none',
      ':hover': {
        textDecoration: 'underline',
      },
    },
  },
  ackGroup: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalL },
  alertSpacer: { marginBottom: tokens.spacingVerticalM },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

export const TypeOfRequestStep: React.FC<TypeOfRequestStepProps> = ({
  formData,
  onFormChange,
  errors = {},
  isUSA = true,
}) => {
  const styles = useStyles();
  const preservationLabelId = useId();
  const preservationDescId = useId();
  const dsaLabelId = useId();
  const dsaDescId = useId();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>Type of Request</h2>
        <p className={styles.muted}>Please provide basic information about your request</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle id={preservationLabelId} className={styles.sectionTitle}>Preservation Request<RequiredIndicator /></CardTitle>
          <CardDescription id={preservationDescId}>Are you requesting preservation of data?</CardDescription>
        </CardHeader>
        <CardContent className={styles.sectionContent}>
          <RadioGroup
            aria-labelledby={preservationLabelId}
            aria-describedby={preservationDescId}
            value={
              formData.isPreservationRequest === null
                ? ''
                : formData.isPreservationRequest.toString()
            }
            onValueChange={(value) => onFormChange({ isPreservationRequest: value === 'true' })}
            items={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
          />
          {errors.isPreservationRequest && (
            <p className={styles.errorText}>{errors.isPreservationRequest}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={styles.emergencyTitle}>
            <AlertTriangle className={styles.emergencyIcon} />
            Emergency Request Notice
          </CardTitle>
        </CardHeader>
        <CardContent className={styles.sectionContent}>
          <p className={styles.emergencyText}>
            Microsoft accepts threat to life emergency submissions via the LE Portal. After you
            submit your emergency form, please send your LNS Reference number to{' '}
            <a href="mailto:lealert@microsoft.com">lealert@microsoft.com</a>, clearly identifying
            the LNS Number and Threat to Life Emergency.
          </p>
        </CardContent>
      </Card>

      {!isUSA && (
        <Card>
          <CardHeader>
            <CardTitle id={dsaLabelId} className={styles.sectionTitle}>DSA Order</CardTitle>
            <CardDescription id={dsaDescId}>
              Is the order you are submitting today an Article 10 DSA-impacted order?
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.sectionContent}>
            <RadioGroup
              aria-labelledby={dsaLabelId}
              aria-describedby={dsaDescId}
              value={formData.isDSAOrder === null ? '' : formData.isDSAOrder.toString()}
              onValueChange={(value) => onFormChange({ isDSAOrder: value === 'true' })}
              items={[
                { value: 'true', label: 'Yes, this is a DSA-impacted order' },
                { value: 'false', label: 'No, this is not a DSA-impacted order' },
              ]}
            />
            {errors.isDSAOrder && <p className={styles.errorText}>{errors.isDSAOrder}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
